import { useCallback, useState } from 'react'
import { useIntl } from 'react-intl'
import { isImageFile } from '../../../../utils/file'
import { exceedMaxSize } from '../../../../utils/attachment'
import TenantStorage from '../../../../utils/storage'
import { generateUuid } from '../../../../utils/uuids'
import { AiAgentImageRef } from '../../../../lib/functions/ai'
import { resizeImageForUpload } from '../imageResize'

// AIエージェントチャットへの画像添付はガントチャートのスクリーンショット数枚程度を想定するため、
// 汎用添付（既定 DEFAULT_MAX_FILE_SIZE_MB=200MB）より小さい上限に絞る。
const MAX_IMAGE_SIZE_MB = 20

type AttachmentStatus = 'uploading' | 'done' | 'error'

export type AiAgentImageAttachment = {
  // アップロード完了前は URL が確定しないため、一覧・削除操作を安定させるための独自キー。
  id: string
  // ローカルプレビュー用の object URL。
  previewUrl: string
  status: AttachmentStatus
}

// アップロード結果を含む添付の状態。送信待ち行列へ移した添付も同じ形で保持する。
export type AiAgentImageAttachmentState = AiAgentImageAttachment & {
  uploadedUrl?: string
}

type UploadedAttachment = AiAgentImageAttachment & {
  status: 'done'
  uploadedUrl: string
}

const isUploaded = (
  attachment: AiAgentImageAttachmentState
): attachment is UploadedAttachment =>
  attachment.status === 'done' && !!attachment.uploadedUrl

// 送信可能な（アップロード完了済みの）画像参照一覧。resize 後は常に JPEG になる。
export const toUploadedImageRefs = (
  attachments: AiAgentImageAttachmentState[]
): AiAgentImageRef[] =>
  attachments.filter(isUploaded).map(attachment => ({
    url: attachment.uploadedUrl,
    mimeType: 'image/jpeg',
  }))

// プレビュー用の object URL を解放する。送信済み・破棄済みで、どこからも参照されなく
// なった添付にだけ使う（行列に積んだ添付は編集で入力欄へ戻るため解放しない）。
export const releaseImageAttachments = (
  attachments: AiAgentImageAttachmentState[]
): void => {
  attachments.forEach(attachment => URL.revokeObjectURL(attachment.previewUrl))
}

/**
 * AIエージェントチャットの画像添付（選択・リサイズ・アップロード・プレビュー・削除）を管理するフック。
 * アップロード完了後は常に JPEG（resizeImageForUpload による再エンコード後）として扱う。
 */
export const useAiAgentImageUpload = () => {
  const intl = useIntl()
  const [attachments, setAttachments] = useState<AiAgentImageAttachmentState[]>(
    []
  )
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  )

  const uploadOne = useCallback(
    async (id: string, file: File) => {
      try {
        const resized = await resizeImageForUpload(file)
        const response = await TenantStorage.uploadFile(resized)
        const uploadedUrl = response.json.downloadUrl
        setAttachments(prev =>
          prev.map(attachment =>
            attachment.id === id
              ? { ...attachment, uploadedUrl, status: 'done' }
              : attachment
          )
        )
      } catch {
        setAttachments(prev =>
          prev.map(attachment =>
            attachment.id === id
              ? { ...attachment, status: 'error' }
              : attachment
          )
        )
        setErrorMessage(
          intl.formatMessage({ id: 'aiAgentChat.attach.error.uploadFailed' })
        )
      }
    },
    [intl]
  )

  const addFiles = useCallback(
    (files: File[]) => {
      setErrorMessage(undefined)
      const imageFiles = files.filter(isImageFile)
      const hasNonImageFile = files.length > imageFiles.length
      if (hasNonImageFile) {
        setErrorMessage(
          intl.formatMessage({ id: 'aiAgentChat.attach.error.notImage' })
        )
      }
      if (imageFiles.length === 0) {
        return
      }
      if (exceedMaxSize(imageFiles, MAX_IMAGE_SIZE_MB)) {
        setErrorMessage(
          intl.formatMessage(
            { id: 'aiAgentChat.attach.error.tooLarge' },
            { maxSizeMB: MAX_IMAGE_SIZE_MB }
          )
        )
        return
      }

      const newAttachments = imageFiles.map(file => ({
        id: generateUuid(),
        previewUrl: URL.createObjectURL(file),
        status: 'uploading' as const,
      }))
      setAttachments(prev => [...prev, ...newAttachments])
      newAttachments.forEach((attachment, index) => {
        uploadOne(attachment.id, imageFiles[index])
      })
    },
    [intl, uploadOne]
  )

  const removeAttachment = useCallback((id: string) => {
    setAttachments(prev => {
      const target = prev.find(attachment => attachment.id === id)
      if (target) {
        URL.revokeObjectURL(target.previewUrl)
      }
      return prev.filter(attachment => attachment.id !== id)
    })
  }, [])

  const clearAttachments = useCallback(() => {
    setAttachments(prev => {
      prev.forEach(attachment => URL.revokeObjectURL(attachment.previewUrl))
      return []
    })
    setErrorMessage(undefined)
  }, [])

  // 添付を送信待ち行列へ移すために取り出す。プレビューは移した先で使い続けるため、
  // clearAttachments と違って object URL を解放しない。
  const detachAttachments = useCallback((): AiAgentImageAttachmentState[] => {
    setAttachments([])
    setErrorMessage(undefined)
    return attachments
  }, [attachments])

  // 送信待ち行列の項目を編集するとき、その添付を入力欄へ戻す。
  // 入力欄にあった添付は行列側へ退避済みのため、解放せずに置き換える。
  const restoreAttachments = useCallback(
    (restored: AiAgentImageAttachmentState[]) => {
      setAttachments(restored)
      setErrorMessage(undefined)
    },
    []
  )

  const isUploading = attachments.some(
    attachment => attachment.status === 'uploading'
  )

  const getUploadedImageRefs = useCallback(
    (): AiAgentImageRef[] => toUploadedImageRefs(attachments),
    [attachments]
  )

  return {
    attachments,
    errorMessage,
    isUploading,
    addFiles,
    removeAttachment,
    clearAttachments,
    detachAttachments,
    restoreAttachments,
    getUploadedImageRefs,
  }
}
